import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import combineStyles from '../../../utils/combineStyles';
import TypographyStyles from '../../../styles/typography';
import LayoutStyles from '../../../styles/layout';
import SpacingStyles from '../../../styles/helpers/spacing';
import PermitButton from './../../Global/Buttons/PermitButton';
import EditButton from './../../Global/Buttons/EditButton';
import {
  formatDate,
  midnightSecondsToHour,
  extractDateTime
} from './../../../utils/DateTime';
import { SHIPMENT_STATUS, DEFAULT_EMPTY } from './../../../constants/constants';
import {
  functionDisabled,
  functionEnabled,
  READ_ONLY_STATUSES,
  buildEstimatedDateString
} from './helpers';
import ActualDateFormModal from './modals/ActualDateForm.modal';
import PlannedDateFormModal from './modals/PlannedDateForm.modal';
import { SHIPMENT_SAVE } from './../../../constants/permissions';

const styles = theme => ({
  sectionTitle: {
    display: 'inline-flex',
    paddingRight: theme.spacing.unit,
    marginBottom: '0 !important'
  },
  secondSection: {
    marginTop: theme.spacing.unit * 2
  },
  readOnlyColor: {
    color: 'rgba(12,31,44,0.38) !important'
  },
  pickedUpButtonContainer: {
    marginTop: theme.spacing.unit * 3
  },
  lateCircle: {
    backgroundColor: '#F22D12',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-flex'
  }
});
export class Pickup extends Component {
  static propTypes = {
    shipmentStatusId: PropTypes.string.isRequired,
    stop: PropTypes.object.isRequired,
    onPlannedDateSave: PropTypes.func.isRequired,
    onActualDateSave: PropTypes.func.isRequired,
    editable: PropTypes.bool.isRequired
  };
  state = {
    displayActualDateModal: false,
    displayPlannedPickupDateModal: false,
    stop: this.props.stop
  };
  componentDidMount() {
    const { stop } = this.state;
    if (stop.actualTimeLocal) {
      const extractedDateTime = extractDateTime(stop.actualTimeLocal);
      stop.actualDate = extractedDateTime.date;
      stop.actualTime = extractedDateTime.time;
    } else {
      stop.actualDate = null;
      stop.actualTime = null;
    }
    this.setState({ stop });
  }
  _toggleDisplayActualDateModal = () => {
    const { displayActualDateModal } = this.state;
    this.setState({ displayActualDateModal: !displayActualDateModal });
  };
  _toggleDisplayPlannedPickupDateModal = () => {
    const { displayPlannedPickupDateModal } = this.state;
    this.setState({
      displayPlannedPickupDateModal: !displayPlannedPickupDateModal
    });
  };

  render() {
    const {
      classes,
      t,
      onPlannedDateSave,
      onActualDateSave,
      shipmentStatusId,
      editable
    } = this.props;
    const {
      stop,
      displayActualDateModal,
      displayPlannedPickupDateModal
    } = this.state;

    const actualPickupDate = formatDate(stop.actualTimeLocal);
    const actualTime = midnightSecondsToHour(stop.actualTime, {
      format: 12,
      displayMinutes: true
    });
    const estimatedPickup = buildEstimatedDateString(
      stop.plannedToTimeLocal,
      stop.plannedFromTimeLocal,
      stop.plannedToTimeLocal
    );
    const emptyElement = (
      <p className={classnames(classes.noMargin, classes.body2)}>--</p>
    );
    const pickupDateNotEditable =
      functionDisabled(shipmentStatusId, [
        SHIPMENT_STATUS.READY_TO_TENDER,
        SHIPMENT_STATUS.EXCEPTION
      ]) || !editable;
    const actualDateNotEditable =
      functionDisabled(shipmentStatusId, [
        SHIPMENT_STATUS.READY_TO_TENDER,
        SHIPMENT_STATUS.EXCEPTION
      ]) || !editable;
    const hideActualPickupDate = functionDisabled(shipmentStatusId, [
      SHIPMENT_STATUS.READY_TO_TENDER,
      SHIPMENT_STATUS.WAITING_FOR_PICKUP
    ]);
    const textColor = READ_ONLY_STATUSES.includes(shipmentStatusId)
      ? classes.readOnlyColor
      : '';

    const showPickedUpButton = functionEnabled(
      shipmentStatusId,
      SHIPMENT_STATUS.WAITING_FOR_PICKUP
    );
    return (
      <div>
        <div>
          <h4
            className={classnames(
              classes.subtitle3,
              classes.sectionTitle,
              classes.boldWeight,
              textColor
            )}
          >
            {t('Planned Pickup')}
          </h4>
          {!pickupDateNotEditable && (
            <EditButton
              onClick={this._toggleDisplayPlannedPickupDateModal}
              permission={SHIPMENT_SAVE}
            />
          )}
        </div>
        <div>
          {estimatedPickup ? (
            <p
              className={classnames(classes.noMargin, classes.body2, textColor)}
            >
              {estimatedPickup}
            </p>
          ) : (
            emptyElement
          )}
        </div>
        <div
          className={classnames(classes.secondSection, {
            [classes.hidden]: hideActualPickupDate
          })}
        >
          <h4
            className={classnames(
              classes.subtitle3,
              classes.sectionTitle,
              textColor,
              classes.boldWeight
            )}
          >
            {t('Actual Pickup')}
          </h4>
          <span
            className={classnames(classes.lateCircle, {
              [classes.hidden]: !stop.carrierWasLate
            })}
          />
          {!actualDateNotEditable && (
            <EditButton
              onClick={this._toggleDisplayActualDateModal}
              permission={SHIPMENT_SAVE}
            />
          )}
        </div>

        {showPickedUpButton && (
          <div className={classes.pickedUpButtonContainer}>
            <PermitButton
              variant="contained"
              color="primary"
              onClick={this._toggleDisplayActualDateModal}
              permission={SHIPMENT_SAVE}
            >
              {t('Picked up')}
            </PermitButton>
          </div>
        )}
        <div
          className={classnames({
            [classes.hidden]: hideActualPickupDate
          })}
        >
          {actualPickupDate !== DEFAULT_EMPTY ? (
            <p
              className={classnames(classes.noMargin, classes.body2, textColor)}
            >
              {`${actualPickupDate} ${actualTime}`}
            </p>
          ) : (
            emptyElement
          )}
        </div>
        <ActualDateFormModal
          open={displayActualDateModal}
          onClose={this._toggleDisplayActualDateModal}
          onSave={onActualDateSave}
          type={'Pickup'}
          stop={stop}
          title={t('When was the shipment picked up?')}
        />
        <PlannedDateFormModal
          open={displayPlannedPickupDateModal}
          title={t('Edit Planned Pickup')}
          type={'Pickup'}
          stop={stop}
          onClose={this._toggleDisplayPlannedPickupDateModal}
          onSave={onPlannedDateSave}
        />
      </div>
    );
  }
}
const translatedPickup = withNamespaces()(Pickup);
export default withStyles(
  combineStyles(styles, TypographyStyles, SpacingStyles, LayoutStyles)
)(translatedPickup);
